import { getAuthInstance } from "../lib/firebase";
import { OPERATION_LOG_API_BASE_URL } from "../constants/api";
import { SerializedTimestamp } from "../types/common";

/**
 * 操作ログ
 */
export interface OperationLog {
  id: string;
  timestamp: SerializedTimestamp;
  operator: {
    uid: string;
    email: string;
    role: string;
  };
  action: string;
  resource: {
    type: string;
    id?: string;
    name?: string;
  };
  description: string;
  details: Record<string, unknown>;
  status: "success" | "error";
  error?: string;
}

/**
 * 認証ヘッダーを取得
 */
async function getAuthHeaders(): Promise<HeadersInit> {
  const auth = getAuthInstance();
  const user = auth.currentUser;

  if (!user) {
    throw new Error("認証されていません");
  }

  const token = await user.getIdToken();

  return {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };
}

/**
 * API エラーをハンドリング
 */
async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: "不明なエラー" }));
    throw new Error(error.error || `HTTP ${response.status}`);
  }
  return response.json();
}

/**
 * ログインログを送信
 */
export async function logLogin(): Promise<void> {
  try {
    const headers = await getAuthHeaders();
    const response = await fetch(`${OPERATION_LOG_API_BASE_URL}/login`, {
      method: "POST",
      headers,
      body: JSON.stringify({
        userAgent: navigator.userAgent,
      }),
    });
    await handleResponse<{ success: boolean }>(response);
  } catch (error) {
    // ログ送信の失敗でログイン自体を妨げないよう、エラーはコンソールに出力するのみにする
    console.error("Failed to log login event:", error);
  }
}

/**
 * 操作ログ一覧を取得
 */
export async function fetchOperationLogs(params: {
  action?: string[];
  resourceType?: string[];
  startDate?: Date | null;
  endDate?: Date | null;
  page?: number;
  pageSize?: number;
}): Promise<{ logs: OperationLog[]; total: number; page: number; pageSize: number }> {
  const headers = await getAuthHeaders();
  const query = new URLSearchParams();
  
  if (params.action && params.action.length > 0) {
    query.append("action", params.action.join(","));
  }
  if (params.resourceType && params.resourceType.length > 0) {
    query.append("resourceType", params.resourceType.join(","));
  }
  if (params.startDate) query.append("startDate", params.startDate.toISOString());
  if (params.endDate) query.append("endDate", params.endDate.toISOString());
  if (params.page) query.append("page", String(params.page));
  if (params.pageSize) query.append("pageSize", String(params.pageSize));

  const response = await fetch(`${OPERATION_LOG_API_BASE_URL}/?${query}`, { headers });
  return handleResponse(response);
}

/**
 * 操作ログ詳細を取得
 */
export async function fetchOperationLogDetail(id: string): Promise<OperationLog> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${OPERATION_LOG_API_BASE_URL}/${id}`, { headers });
  return handleResponse(response);
}
